import React, { FC, useMemo } from 'react';
import ChapterNavigationCard from 'components/reports/ChapterNavigationCard';
import styles from './StartPage.module.scss';
import { useAppDispatch } from 'app/store';
import { useSelector } from 'react-redux';
import { selectInfoUserSelector } from 'features/user/userSlice';
import { USER_ROLE } from 'features/user/userTypes';
import Timesheets from 'pages/Timesheets';

type Card = {
  title: string;
  description: string;
  link: string;
};

type CardCategory = {
  category: string;
  cards: Card[];
};

const StartPage: FC = () => {
  const dispatch = useAppDispatch();
  const userInfo = useSelector(selectInfoUserSelector);
  const role = userInfo?.role;

  const categorizedCards: CardCategory[] = useMemo(() => {
    if (role === USER_ROLE.Owner || role === USER_ROLE.Admin) {
      return [
        {
          category: 'Timesheets',
          cards: [
            {
              title: 'Calendar/Timesheets',
              description: 'Log your hours or review your team’s timesheets',
              link: '/time-entries',
            },
          ]
        },
        {
          category: 'Management',
          cards: [
            {
              title: 'Projects',
              description: 'Organize your projects',
              link: '/projects',
            },
            {
              title: 'Clients',
              description: 'Manage your client list',
              link: '/clients',
            },
            {
              title: 'Members',
              description: 'Invite or remove team members, set rates, change roles, and assign them to projects',
              link: '/members',
            },
          ],
        },
        {
          category: 'Reports',
          cards: [
            {
              title: 'Time & Activity Report',
              description: 'Send this report to clients to show your team’s activity and project costs',
              link: 'reports/time-and-activity',
            },
            {
              title: 'Amounts Owed Report',
              description: 'Help your team understand their tracked time and what it’s worth',
              link: 'reports/amounts-owed',
            },
          ],
        },
        {
          category: 'Time-off',
          cards: [
            {
              title: 'Policies',
              description: 'Create and manage custom company policies',
              link: 'time-off/policies',
            },
            {
              title: 'Holidays',
              description: 'Set up holidays to reflect non-working days',
              link: 'time-off/holidays',
            },
            {
              title: 'Time-off Requests',
              description: 'Manage all time-off requests in one place',
              link: 'time-off/requests',
            }
          ],
        },
      ];
    }

    if (role === USER_ROLE.User) {
      return [
        {
          category: 'Timesheets',
          cards: [
            {
              title: 'Calendar/Timesheets',
              description: 'Track your working hours with ease',
              link: '/time-entries',
            },
          ],
        },
        {
          category: 'Management',
          cards: [
            {
              title: 'Projects',
              description: 'See which projects you’re involved in',
              link: '/projects',
            },
          ],
        },
        {
          category: 'Reports',
          cards: [
            {
              title: 'Amounts Owed Report',
              description: 'See what you worked on and how much you made - all in one report',
              link: 'reports/amounts-owed/my',
            }
          ],
        },
        {
          category: 'Time-off',
          cards: [
            {
              title: 'Time-off requests',
              description: 'Submit your time-off requests',
              link: 'time-off/requests',
            }
          ],
        },
      ];
    }

    if (role === USER_ROLE.Viewer) {
      return [
        {
          category: 'Timesheets',
          cards: [
            {
              title: 'Calendar/Timesheets',
              description: 'View your team’s timesheets',
              link: '/time-entries',
            },
          ],
        },
        {
          category: 'Management',
          cards: [
            {
              title: 'Projects',
              description: 'View the list of your projects',
              link: '/projects',
            },
          ],
        },
        {
          category: 'Reports',
          cards: [
            {
              title: 'Time & Activity Report',
              description: 'See what your team worked on - by group or by person',
              link: 'reports/time-and-activity',
            },
          ],
        }
      ];
    }

    return [];
  }, [role]);

  return (
    <div className={styles.root}>
      {categorizedCards.map((section) => (
        <div key={section.category} className={styles.section}>
          <p className={styles.sectionTitle}>{section.category}</p>
          <div className={styles.navigationBlock}>
            {section.cards.map((card) => (
              <ChapterNavigationCard
                key={card.title}
                title={card.title}
                description={card.description}
                link={card.link}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default StartPage;
